<script setup lang="ts">
  import type { AccordionWithDynamicImageStoryblok } from '@/storyblok/component-types-sb'
  import { toAssetImageProps } from '@/utils/storyblok/normalizers'

  const props = defineProps<{
    blok: AccordionWithDynamicImageStoryblok
  }>()

  const activeAccordionValue = ref<string>(props.blok.items?.[0]?._uid ?? '')

  const currentItem = computed(() => props.blok.items?.find((item) => item._uid === activeAccordionValue.value))
  const imageProps = computed(() => toAssetImageProps(currentItem.value?.image))

  const { isDesktop, isTablet } = useBTBreakpoints()

  const imageWidth = computed(() => {
    if (isDesktop.value) return 456
    if (isTablet.value) return 696

    return 288
  })

  const imageHeight = computed(() => {
    if (isDesktop.value) return 531
    if (isTablet.value) return 284

    return 192
  })
</script>

<template>
  <section class="md:mx-auto md:my-6 lg:w-full">
    <div class="w-full gap-x-24 gap-y-10 md:mx-auto md:mb-4 md:flex md:gap-x-12 md:space-y-0 lg:w-10/12 xl:gap-x-24">
      <div class="mb-10 flex flex-col sm:mx-auto md:mb-0 md:w-full xl:w-full">
        <AccordionRoot
          v-model="activeAccordionValue"
          :collapsible="false"
          type="single"
          class="w-full divide-y divide-divider border-y border-divider xl:max-w-144"
        >
          <AccordionItem
            v-for="item in blok.items"
            :key="item._uid"
            :value="item._uid"
            class="data-[state=open]:bg-grey"
          >
            <AccordionHeader class="mb-0">
              <AccordionTrigger
                class="group mb-0 flex w-full items-center justify-between px-4 py-4 text-left sm:py-5 md:px-6 md:py-3"
              >
                <span class="w-1/2 text-bt-blue-ui text-subtitle-1 md:w-full">
                  {{ item.title }}
                </span>

                <div class="flex size-8 items-center justify-center rounded-full border border-bt-blue-ui pb-0 lg:p-1">
                  <Icon
                    name="common/add"
                    class="text-bt-blue-ui transition duration-300 group-data-[state=open]:hidden"
                  />
                  <Icon
                    name="common/remove"
                    class="hidden text-bt-blue-ui transition duration-300 group-data-[state=open]:inline"
                  />
                </div>
              </AccordionTrigger>
            </AccordionHeader>

            <AccordionContent class="px-4 pb-6 md:px-6 md:py-4 lg:pb-5 lg:pt-0">
              <p class="text-bt-dark-blue text-body-1">
                {{ item.description }}
              </p>

              <div
                v-if="!isDesktop"
                class="flex flex-col space-y-4"
              >
                <LazyNuxtImg
                  v-if="imageProps.src"
                  :src="imageProps.src"
                  :alt="imageProps.alt"
                  :width="imageWidth"
                  :height="imageHeight"
                  class="w-full rounded-md object-cover lg:rounded-lg"
                  provider="storyblok"
                />

                <p
                  v-if="blok.image_disclaimer"
                  class="max-w-120 text-grey-secondary text-body-1 sm:max-w-full"
                >
                  {{ blok.image_disclaimer }}
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>
        </AccordionRoot>
      </div>

      <div
        v-if="isDesktop"
        class="flex flex-col items-end space-y-6 md:w-2/5 lg:w-1/2"
      >
        <LazyNuxtImg
          v-if="imageProps.src"
          :src="imageProps.src"
          :alt="imageProps.alt"
          :width="imageWidth"
          :height="imageHeight"
          class="rounded-md object-cover lg:rounded-lg"
          provider="storyblok"
        />

        <p
          v-if="blok.image_disclaimer"
          class="w-120 text-grey-secondary text-body-1 lg:w-114"
        >
          {{ blok.image_disclaimer }}
        </p>
      </div>
    </div>
  </section>
</template>
